'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { Mail, Phone, MapPin, MessageSquare, CheckCircle, Loader2 } from 'lucide-react';

interface ContactFormData {
  name: string;
  email: string;
  mobile: string;
  city: string;
  message: string;
}

export default function ContactPage() {
  const [form, setForm] = useState<ContactFormData>({ name: '', email: '', mobile: '', city: '', message: '' });
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<ContactFormData> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Valid email is required';
    if (!form.mobile.trim() || !/^[6-9]\d{9}$/.test(form.mobile.replace(/\s/g, ''))) newErrors.mobile = 'Valid 10-digit mobile number required';
    if (!form.city.trim()) newErrors.city = 'City is required';
    if (!form.message.trim() || form.message.trim().length < 10) newErrors.message = 'Message must be at least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const supabase = createClient();
      await supabase.from('contact_inquiries').insert({
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
      });
    } catch {
      // silent — show success regardless
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setForm({ name: '', email: '', mobile: '', city: '', message: '' });
      }, 3000);
    }
  };

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={26} className="text-primary" />
          </div>
          <h1 className="text-3xl font-extrabold text-foreground mb-2">Contact Us</h1>
          <p className="text-muted-foreground text-sm max-w-md mx-auto">
            Have a question or need help? Fill in the form below and our team will get back to you within 24 hours.
          </p>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { icon: Mail, label: 'Email', value: 'support@tolettu.com' },
            { icon: Phone, label: 'Phone', value: '+91 6281169720' },
            { icon: MapPin, label: 'Location', value: 'Hyderabad, India' },
          ].map((item) => (
            <div key={item.label} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-muted/50 border border-border text-center">
              <item.icon size={18} className="text-primary" />
              <div className="text-xs font-bold text-foreground">{item.label}</div>
              <div className="text-xs text-muted-foreground">{item.value}</div>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-card rounded-2xl border border-border p-6 shadow-sm relative overflow-hidden">
          {/* Thank you overlay */}
          {submitted && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-card rounded-2xl fade-in">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="text-xl font-extrabold text-foreground mb-2 text-center">Thank you for your inquiry!</h2>
              <p className="text-muted-foreground text-sm text-center max-w-xs">
                We will connect you within 24 hours.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Name */}
            <div>
              <label htmlFor="contact-name" className="label-text">Full Name</label>
              <input
                id="contact-name"
                type="text"
                placeholder="Your full name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`input-field mt-1 ${errors.name ? 'border-red-400' : ''}`}
                suppressHydrationWarning
              />
              {errors.name && <p className="error-text">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="contact-email" className="label-text">Email Address</label>
              <input
                id="contact-email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`input-field mt-1 ${errors.email ? 'border-red-400' : ''}`}
                suppressHydrationWarning
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>

            {/* Mobile */}
            <div>
              <label htmlFor="contact-mobile" className="label-text">Mobile Number</label>
              <div className="relative mt-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">+91</span>
                <input
                  id="contact-mobile"
                  type="tel"
                  placeholder="98765 43210"
                  value={form.mobile}
                  onChange={(e) => handleChange('mobile', e.target.value)}
                  className={`input-field pl-12 ${errors.mobile ? 'border-red-400' : ''}`}
                  suppressHydrationWarning
                />
              </div>
              {errors.mobile && <p className="error-text">{errors.mobile}</p>}
            </div>

            {/* City */}
            <div>
              <label htmlFor="contact-city" className="label-text">City</label>
              <input
                id="contact-city"
                type="text"
                placeholder="e.g. Hyderabad"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={`input-field mt-1 ${errors.city ? 'border-red-400' : ''}`}
                suppressHydrationWarning
              />
              {errors.city && <p className="error-text">{errors.city}</p>}
            </div>

            {/* Message */}
            <div>
              <label htmlFor="contact-message" className="label-text">Your Message</label>
              <textarea
                id="contact-message"
                rows={4}
                placeholder="Ask us anything about listings, pricing, or support..."
                value={form.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className={`input-field mt-1 resize-none ${errors.message ? 'border-red-400' : ''}`}
                suppressHydrationWarning
              />
              {errors.message && <p className="error-text">{errors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center h-11 text-base"
              suppressHydrationWarning
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Sending...</>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}
